import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, ClipboardPaste, X } from "lucide-react";
import api from "../../services/api";
import "./LegalEditor.scss";

// Parse a pasted legal document into the editor's structured shape.
// Tolerant of common formats: numbered "1. Section", "**1. Section**",
// markdown bold around titles, "Effective Date:" line in body or header.
// Lines before the first numbered section become the intro; the document
// title line (e.g. "PRIVACY POLICY FOR THE ZONA APP") is filtered out.
function parseLegalDocument(text) {
    const stripBold = (s) => s.replace(/^\*\*+/, "").replace(/\*\*+$/, "").trim();
    // Body content is rendered escape-html on website + mobile, so any
    // remaining ** in intro/body would show as literal asterisks. Strip
    // pairs and leftovers so pasted markdown drafts render as clean text.
    const stripMarkdown = (s) =>
        String(s)
            .replace(/\*\*+([^*\n]+)\*\*+/g, "$1") // **bold** → bold
            .replace(/__([^_\n]+)__/g, "$1")         // __bold__ → bold
            .replace(/\*\*+/g, "")                   // any leftover **
            .replace(/\\([_*])/g, "$1");             // unescape \* and \_
    const lines = String(text || "").split(/\r?\n/);

    let effectiveDate = "";
    let intro = "";
    let docTitleSeen = false;
    const sections = [];
    let current = null;

    const sectionRe = /^\s*(?:\*\*)?(\d{1,3})[\.\)]\s+(.+?)\s*$/;
    const titleHeaderRe = /^(PRIVACY POLICY|TERMS AND CONDITIONS|TERMS & CONDITIONS|TERMS OF SERVICE|CONTENT POLICY)\b.*$/i;
    const effectiveRe = /^[\*\s_>#-]*Effective Date:?\s*(.+?)\s*\**\s*$/i;

    for (const raw of lines) {
        const trimmed = raw.trim();

        // Effective date can appear anywhere; capture and skip the line.
        const effMatch = trimmed.match(effectiveRe);
        if (effMatch && !effectiveDate) {
            effectiveDate = stripBold(effMatch[1]).replace(/^\*+|\*+$/g, "").trim();
            continue;
        }

        // First all-caps doc title line is metadata, skip it.
        if (!docTitleSeen && titleHeaderRe.test(trimmed)) {
            docTitleSeen = true;
            continue;
        }

        // Numbered section heading
        const secMatch = trimmed.match(sectionRe);
        if (secMatch) {
            // commit previous
            if (current) sections.push({ ...current, body: current.body.trim() });
            current = {
                number: secMatch[1],
                title: stripBold(secMatch[2]).replace(/[:\.]\s*$/, ""),
                body: "",
            };
            continue;
        }

        // Body / intro accumulation. Preserve blank lines as paragraph breaks.
        if (current) {
            current.body += (current.body ? "\n" : "") + raw;
        } else {
            intro += (intro ? "\n" : "") + raw;
        }
    }
    if (current) sections.push({ ...current, body: current.body.trim() });

    // Renumber sequentially in case the input had gaps or restarts.
    const renumbered = sections.map((s, i) => ({ ...s, number: String(i + 1) }));

    return {
        effectiveDate,
        intro: intro.trim(),
        sections: renumbered,
    };
}

// Structured editor for Terms/Privacy-style legal documents.
// Saves as { effectiveDate, intro, sections: [{ number, title, body }] } to
// the backend's /legal/:type/:document endpoint. Live preview on the right
// renders the same structure the website will render.

const SURFACE_LABELS = {
    app: "Mobile App",
    business: "Business Panel",
    zonavip: "ZONA VIP",
    brandambassador: "Brand Ambassador",
    advertiser: "Ads Portal",
    promoter: "Promoter Portal",
};

export default function LegalEditor({ type, document, kind }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [effectiveDate, setEffectiveDate] = useState("");
    const [intro, setIntro] = useState("");
    const [sections, setSections] = useState([]);
    const [dirty, setDirty] = useState(false);
    const initialRef = useRef(null);

    const [pasteOpen, setPasteOpen] = useState(false);
    const [pasteText, setPasteText] = useState("");
    const [pastePreview, setPastePreview] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/legal/${type}/${document}`);
            const next = {
                effectiveDate: data.effectiveDate || "",
                intro: data.intro || "",
                sections: Array.isArray(data.sections) ? data.sections : [],
            };
            setEffectiveDate(next.effectiveDate);
            setIntro(next.intro);
            setSections(next.sections);
            initialRef.current = JSON.stringify(next);
            setDirty(false);
        } catch (err) {
            if (err?.response?.status === 404) {
                setEffectiveDate("");
                setIntro("");
                setSections([]);
                initialRef.current = JSON.stringify({ effectiveDate: "", intro: "", sections: [] });
                setDirty(false);
            } else {
                console.error(err);
                toast.error(`Failed to load ${kind}`);
            }
        } finally {
            setLoading(false);
        }
    }, [type, document, kind]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (initialRef.current == null) return;
        const current = JSON.stringify({ effectiveDate, intro, sections });
        setDirty(current !== initialRef.current);
    }, [effectiveDate, intro, sections]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                effectiveDate,
                intro,
                sections: sections
                    .map((s, i) => ({
                        number: String(s.number ?? i + 1).trim(),
                        title: String(s.title ?? "").trim(),
                        body: String(s.body ?? ""),
                    }))
                    .filter((s) => s.title),
            };
            await api.put(`/legal/${type}/${document}`, payload);
            initialRef.current = JSON.stringify({ effectiveDate, intro, sections });
            setDirty(false);
            toast.success(`${kind} saved — live on app and website`);
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.error || `Failed to save ${kind}`);
        } finally {
            setSaving(false);
        }
    };

    const addSection = () => {
        setSections([
            ...sections,
            { number: String(sections.length + 1), title: "", body: "" },
        ]);
    };
    const removeSection = (i) => {
        const next = sections.filter((_, idx) => idx !== i);
        setSections(renumber(next));
    };
    const moveSection = (i, dir) => {
        const next = [...sections];
        const target = i + dir;
        if (target < 0 || target >= next.length) return;
        [next[i], next[target]] = [next[target], next[i]];
        setSections(renumber(next));
    };
    const updateSection = (i, field, value) => {
        const next = sections.map((s, idx) => (idx === i ? { ...s, [field]: value } : s));
        setSections(next);
    };
    const renumber = (list) => list.map((s, idx) => ({ ...s, number: String(idx + 1) }));

    const openPaste = () => {
        setPasteText("");
        setPastePreview(null);
        setPasteOpen(true);
    };
    const previewPaste = () => {
        const parsed = parseLegalDocument(pasteText);
        setPastePreview(parsed);
    };
    const applyPaste = () => {
        if (!pastePreview) return;
        setEffectiveDate(pastePreview.effectiveDate || "");
        setIntro(pastePreview.intro || "");
        setSections(pastePreview.sections || []);
        setPasteOpen(false);
        setPasteText("");
        setPastePreview(null);
        toast.success(`Imported ${pastePreview.sections?.length || 0} sections — review and Save when ready`);
    };

    if (loading) {
        return <div className="legal-editor legal-editor--loading">Loading {kind}…</div>;
    }

    return (
        <div className="legal-editor">
            <div className="legal-editor__header">
                <div>
                    <div className="legal-editor__label">{SURFACE_LABELS[type] || type} · {kind}</div>
                    <div className="legal-editor__hint">Changes save to the same source that mobile app and website read from.</div>
                </div>
                <button
                    className="legal-editor__save"
                    onClick={save}
                    disabled={!dirty || saving}
                >
                    <Save size={16} />
                    {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
                </button>
            </div>

            <div className="legal-editor__split">
                <div className="legal-editor__pane legal-editor__pane--edit">
                    <label className="legal-editor__field">
                        <span>Effective date</span>
                        <input
                            type="text"
                            value={effectiveDate}
                            onChange={(e) => setEffectiveDate(e.target.value)}
                            placeholder="e.g. Oct 24th, 2025"
                        />
                    </label>

                    <label className="legal-editor__field">
                        <span>Intro paragraph</span>
                        <textarea
                            value={intro}
                            onChange={(e) => setIntro(e.target.value)}
                            rows={5}
                            placeholder="The opening paragraph before section 1. Shows at the top of the document."
                        />
                    </label>

                    <div className="legal-editor__sections-header">
                        <h3>Sections</h3>
                        <div style={{ display: "flex" }}>
                            <button className="legal-editor__paste-trigger" onClick={openPaste} title="Paste a full legal document and let it auto-format into sections">
                                <ClipboardPaste size={14} /> Paste full document
                            </button>
                            <button className="legal-editor__add" onClick={addSection}>
                                <Plus size={14} /> Add section
                            </button>
                        </div>
                    </div>

                    {sections.length === 0 && (
                        <div className="legal-editor__empty">No sections yet. Click "Add section" to start.</div>
                    )}

                    {sections.map((s, i) => (
                        <div key={i} className="legal-editor__section">
                            <div className="legal-editor__section-head">
                                <div className="legal-editor__section-num">{String(i + 1).padStart(2, "0")}</div>
                                <input
                                    className="legal-editor__section-title"
                                    value={s.title}
                                    onChange={(e) => updateSection(i, "title", e.target.value)}
                                    placeholder="Section title (e.g. User Responsibilities)"
                                />
                                <div className="legal-editor__section-actions">
                                    <button onClick={() => moveSection(i, -1)} disabled={i === 0} title="Move up"><ArrowUp size={14} /></button>
                                    <button onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1} title="Move down"><ArrowDown size={14} /></button>
                                    <button onClick={() => removeSection(i)} title="Delete" className="legal-editor__section-delete"><Trash2 size={14} /></button>
                                </div>
                            </div>
                            <textarea
                                className="legal-editor__section-body"
                                value={s.body}
                                onChange={(e) => updateSection(i, "body", e.target.value)}
                                rows={Math.max(3, (s.body.match(/\n/g)?.length || 0) + 2)}
                                placeholder="Section body — supports line breaks. Each paragraph shows as its own block on the website."
                            />
                        </div>
                    ))}
                </div>

                <div className="legal-editor__pane legal-editor__pane--preview">
                    <div className="legal-editor__preview-label">Live preview — matches the website</div>
                    <div className="legal-editor__preview">
                        <div className="legal-editor__preview-title">{kind === "Terms" ? "Terms & Conditions" : kind === "Privacy" ? "Privacy Policy" : kind}</div>
                        {effectiveDate && <div className="legal-editor__preview-meta">Effective: {effectiveDate}</div>}
                        {intro && intro.split(/\n+/).filter(Boolean).map((p, i) => (
                            <p key={`intro-${i}`} className="legal-editor__preview-p">{p}</p>
                        ))}
                        {sections.map((s, i) => (
                            <div key={`sec-${i}`} className="legal-editor__preview-section">
                                <h2 className="legal-editor__preview-h2">
                                    <span className="legal-editor__preview-num">{String(i + 1).padStart(2, "0")}</span>
                                    {s.title || <em className="legal-editor__preview-untitled">(untitled section)</em>}
                                </h2>
                                {s.body.split(/\n+/).filter(Boolean).map((p, j) => (
                                    <p key={`sec-${i}-p-${j}`} className="legal-editor__preview-p">{p}</p>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {pasteOpen && (
                <div className="legal-editor__modal-backdrop" onClick={() => setPasteOpen(false)}>
                    <div className="legal-editor__modal" onClick={(e) => e.stopPropagation()}>
                        <div className="legal-editor__modal-header">
                            <h3>Paste full {kind?.toLowerCase() || "document"}</h3>
                            <button className="legal-editor__modal-close" onClick={() => setPasteOpen(false)} title="Close">
                                <X size={16} />
                            </button>
                        </div>
                        <div className="legal-editor__modal-body">
                            <div className="legal-editor__modal-hint">
                                Paste the whole document below. The parser will pick up the Effective Date line, the intro paragraphs, and any numbered sections (1., 2., etc.). Markdown bold (**Label:**) is stripped automatically. Click <strong>Preview</strong> to verify the parse, then <strong>Apply</strong> to load it into the editor.
                            </div>
                            <textarea
                                className="legal-editor__paste-textarea"
                                placeholder={"Paste here. Example:\n\nEffective Date: May 11, 2026\n\nIntro paragraph text...\n\n1. First Section\nSection body text...\n\n2. Second Section\n..."}
                                value={pasteText}
                                onChange={(e) => setPasteText(e.target.value)}
                            />
                            {pastePreview && (
                                <div className="legal-editor__paste-preview">
                                    <h4>Parsed preview</h4>
                                    <div className="preview-row">
                                        <span className="preview-num">Effective:</span> {pastePreview.effectiveDate || <em style={{ opacity: 0.4 }}>not detected</em>}
                                    </div>
                                    <div className="preview-row">
                                        <span className="preview-num">Intro:</span> {pastePreview.intro ? `${pastePreview.intro.slice(0, 120)}${pastePreview.intro.length > 120 ? "…" : ""}` : <em style={{ opacity: 0.4 }}>none</em>}
                                    </div>
                                    <div className="preview-row" style={{ marginTop: 12, fontWeight: 600 }}>
                                        Sections ({pastePreview.sections?.length || 0}):
                                    </div>
                                    {pastePreview.sections?.map((s, i) => (
                                        <div key={i} className="preview-row">
                                            <span className="preview-num">{String(i + 1).padStart(2, "0")}</span>
                                            {s.title}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="legal-editor__modal-footer">
                            <button onClick={() => setPasteOpen(false)}>Cancel</button>
                            <button onClick={previewPaste} disabled={!pasteText.trim()}>Preview</button>
                            <button className="primary" onClick={applyPaste} disabled={!pastePreview || !pastePreview.sections?.length}>
                                Apply to editor
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
